import { useEffect, useCallback } from "react"
import { useStore } from "react-redux"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias } from "resources/account"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { MODULE_TYPES, type DashboardModule } from "../modules/types"
import { useDeletePinnedWorkflow } from "connected-components/ai-teammate/hooks/use-delete-pinned-workflow"
import { useDashboardId } from "../context/dashboard-id"
import { EMPTY_MODULES } from "../store/panels-slice"
import { log } from "helpers/logger"
import { warn } from "helpers/dev-logger"
import type { ReduxState } from "model/state"

/**
 * Hook that listens for module removal events and handles cleanup for AI artifacts
 */
export function useRemoveArtifactHandler(): void {
  const { mutateAsync: deletePinnedWorkflow } = useDeletePinnedWorkflow()
  const accountAlias = useAccountAlias()
  const queryClient = useQueryClient()
  const store = useStore<ReduxState>()
  const dashboardId = useDashboardId()

  const handleModuleRemoved = useCallback(async (eventModule: DashboardModule) => {
    if (eventModule.type !== MODULE_TYPES.AI_ARTIFACT) {
      return
    }

    // Read directly from store — same pattern as use-pin-artifact-handler.
    // store.getState() is always current regardless of React's render cycle.
    const currentModules = store.getState().dashboard.panels[dashboardId]?.modules ?? EMPTY_MODULES
    const currentModule = currentModules.find((m) => m.id === eventModule.id)
    const module = currentModule || eventModule
    const pinnedId = module.config?.pinnedId
    const isPinning = module.config?.isPinning

    if (pinnedId) {
      queryClient.removeQueries({ queryKey: ["ai-artifact", pinnedId] })

      try {
        await deletePinnedWorkflow({
          accountAlias,
          pinnedId,
        })
      } catch (error) {
        log(`Failed to delete pinned workflow: ${error}`)
      }
    }

    if (isPinning) {
      warn("AI artifact module removed while pin was in progress")
    }
  }, [store, dashboardId, deletePinnedWorkflow, accountAlias, queryClient])

  useEffect(() => {
    const cleanupFn = dashboardEvents.on<DashboardModule>(
      DASHBOARD_EVENTS.MODULE_REMOVED,
      handleModuleRemoved,
    )

    return cleanupFn
  }, [handleModuleRemoved])
}
